<template>
  <div
    :class="{
      'srp-segmented-control': true,
      'srp-segmented-control--sm': props.size === 'sm',
      'srp-segmented-control--md': props.size === 'md',
      'srp-segmented-control--full-width': props.fullWidth,
      'srp-segmented-control--disabled': props.disabled,
    }"
    role="radiogroup"
    :aria-label="props.ariaLabel"
  >
    <button
      v-for="(option, index) in props.options"
      :key="`option-${option.value}`"
      :class="{
        'srp-segmented-control__segment': true,
        'srp-segmented-control__segment--selected': isSelected(option),
        'srp-segmented-control__segment--disabled': props.disabled || option.disabled,
      }"
      role="radio"
      :aria-checked="isSelected(option)"
      :tabindex="getTabIndex(option)"
      :disabled="props.disabled || option.disabled"
      @click="handleClick(option)"
      @keydown="handleKeydown($event, option, index)"
    >
      <IconEmbedded v-if="option.icon" :name="option.icon" :size="getIconSize()" />
      <span class="srp-segmented-control__label">{{ option.label }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import { IconName } from "@contracts/IconName";

export type SegmentedOption = {
  value: string | number;
  label: string;
  icon?: IconName;
  disabled?: boolean;
};

const props = withDefaults(
  defineProps<{
    modelValue: string | number;
    options: SegmentedOption[];
    disabled?: boolean;
    size?: "sm" | "md";
    fullWidth?: boolean;
    ariaLabel?: string;
  }>(),
  {
    disabled: false,
    size: "md",
    fullWidth: false,
    ariaLabel: undefined,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number): void;
  (e: "change", payload: { newValue: string | number; oldValue: string | number }): void;
}>();

// Check if option is selected
function isSelected(option: SegmentedOption): boolean {
  return props.modelValue === option.value;
}

// Get tabindex (roving tabindex pattern - only selected item is focusable, or first enabled if none selected)
function getTabIndex(option: SegmentedOption): number {
  if (props.disabled || option.disabled) {
    return -1;
  }
  const selectedIndex = props.options.findIndex(opt => isSelected(opt) && !opt.disabled);
  const firstEnabledIndex = props.options.findIndex(opt => !opt.disabled);
  
  if (selectedIndex >= 0) {
    return props.options.indexOf(option) === selectedIndex ? 0 : -1;
  }
  return props.options.indexOf(option) === firstEnabledIndex ? 0 : -1;
}

// Handle click
function handleClick(option: SegmentedOption) {
  if (props.disabled || option.disabled || isSelected(option)) {
    return;
  }
  
  const oldValue = props.modelValue;
  emit("update:modelValue", option.value);
  emit("change", { newValue: option.value, oldValue });
}

// Handle keyboard navigation
function handleKeydown(event: KeyboardEvent, option: SegmentedOption, index: number) {
  if (props.disabled) {
    return;
  }

  const enabledOptions = props.options.filter(opt => !opt.disabled);
  const currentEnabledIndex = enabledOptions.findIndex(opt => opt.value === option.value);

  switch (event.key) {
    case "ArrowLeft":
    case "ArrowUp": {
      event.preventDefault();
      const prevIndex = currentEnabledIndex > 0 ? currentEnabledIndex - 1 : enabledOptions.length - 1;
      const prevOption = enabledOptions[prevIndex];
      if (prevOption) {
        handleClick(prevOption);
        // Focus the button
        const buttons = (event.target as HTMLElement).parentElement?.querySelectorAll("button");
        const targetButton = Array.from(buttons || []).find(
          btn => btn.getAttribute("aria-checked") === "true"
        ) as HTMLButtonElement;
        targetButton?.focus();
      }
      break;
    }
    case "ArrowRight":
    case "ArrowDown": {
      event.preventDefault();
      const nextIndex = currentEnabledIndex < enabledOptions.length - 1 ? currentEnabledIndex + 1 : 0;
      const nextOption = enabledOptions[nextIndex];
      if (nextOption) {
        handleClick(nextOption);
        // Focus the button
        const buttons = (event.target as HTMLElement).parentElement?.querySelectorAll("button");
        const targetButton = Array.from(buttons || []).find(
          btn => btn.getAttribute("aria-checked") === "true"
        ) as HTMLButtonElement;
        targetButton?.focus();
      }
      break;
    }
    case "Home": {
      event.preventDefault();
      const firstOption = enabledOptions[0];
      if (firstOption) {
        handleClick(firstOption);
        const buttons = (event.target as HTMLElement).parentElement?.querySelectorAll("button");
        const targetButton = Array.from(buttons || []).find(
          btn => btn.getAttribute("aria-checked") === "true"
        ) as HTMLButtonElement;
        targetButton?.focus();
      }
      break;
    }
    case "End": {
      event.preventDefault();
      const lastOption = enabledOptions[enabledOptions.length - 1];
      if (lastOption) {
        handleClick(lastOption);
        const buttons = (event.target as HTMLElement).parentElement?.querySelectorAll("button");
        const targetButton = Array.from(buttons || []).find(
          btn => btn.getAttribute("aria-checked") === "true"
        ) as HTMLButtonElement;
        targetButton?.focus();
      }
      break;
    }
    case "Enter":
    case " ": {
      event.preventDefault();
      if (!isSelected(option)) {
        handleClick(option);
      }
      break;
    }
  }
}

// Get icon size based on component size
function getIconSize(): number {
  return props.size === "sm" ? 14 : 18;
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Segmented Control ===========================================================
.srp-segmented-control {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 100px;
  background: rgba(231, 237, 240, 0.6);
  position: relative;

  &--full-width {
    display: flex;
    width: 100%;
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  // Size variants
  &--sm {
    padding: 3px;
  }

  &--md {
    padding: 4px;
  }

  // Segment ===================================================================
  &__segment {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: none;
    outline: none;
    border-radius: 100px;
    background: transparent;
    cursor: pointer;
    user-select: none;
    transition: all 0.15s ease;
    position: relative;
    white-space: nowrap;
    font-family: $font-family;
    font-weight: 500;

    // Focus styles
    &:focus-visible {
      outline: 2px solid rgba(133, 183, 217, 0.8);
      outline-offset: 2px;
    }

    // Size variants
    .srp-segmented-control--sm & {
      height: 28px;
      min-height: 28px;
      padding: 0 12px;
      font-size: 13px;
      line-height: 13px;
      gap: 5px;
    }

    .srp-segmented-control--md & {
      height: 36px;
      min-height: 36px;
      padding: 0 16px;
      font-size: 14px;
      line-height: 14px;
      gap: 6px;
    }

    // Full width: segments distribute evenly
    .srp-segmented-control--full-width & {
      flex: 1;
    }

    // Unselected state
    color: #5b5b5b;

    :deep(svg) {
      fill: #5b5b5b;
      transition: fill 0.15s ease;
    }

    // Hover on unselected
    &:hover:not(&--selected):not(&--disabled) {
      background: rgba(201, 207, 210, 0.5);
    }

    // Selected state
    &--selected {
      background: #118689;
      color: white;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);

      :deep(svg) {
        fill: white;
      }
    }

    // Disabled state
    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }

    // Tabindex -1 means not keyboard focusable
    &[tabindex="-1"] {
      outline: none;
    }
  }

  // Label ======================================================================
  &__label {
    display: flex;
    align-items: center;
  }
}
</style>
